import React, { useState } from 'react';
import i18n from 'i18next';
import { ContentDto } from 'models/dtoTypes';
import GeneralConfigContext from 'data/GeneralConfigContext';
import Splash from 'components/Splash';
import { GetContent } from 'services/openapiClient';
import { CurrentUser } from 'auth';
import generateResources from 'utils/generateResources';
import generateNonce from 'utils/generateNonce';
import { Box, Modal, Typography } from '@mui/material';

const MINUTE = 60 * 1000;

const style = {
  position: 'absolute',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'white',
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 2,
  outline: 'none',
  borderRadius: 5,
};

export default function ContentProvider({ children }: { children: JSX.Element }): JSX.Element {
  const defaultLocaleCode = CurrentUser.getInstance().getLocale();
  const [localeCode, setLocaleCode] = useState<string>(defaultLocaleCode);
  const [nonce, setNonce] = useState<string>(generateNonce());
  const [resources, setResources] = useState<ContentDto[]>([]);
  const [resourceReady, setResourceReady] = useState(false);
  const [showReloadPopup, setShowReloadPopup] = useState<boolean>(false);

  const checkScripts = () => {
    const scripts: string[] = [];
    document.querySelectorAll('script[src]').forEach((script) => {
      scripts.push(script.getAttribute('src') as string);
    });

    if (scripts.length) {
      Promise.all(
        scripts.map((script) =>
          fetch(script, {
            method: 'GET',
          }).then((res) => res.ok),
        ),
      ).then((responses) => {
        if (responses.every((item) => !item)) {
          setShowReloadPopup(true);
        } else {
          showReloadTimeout();
        }
      });
    } else {
      setTimeout(checkScripts, 0.1 * MINUTE); // no scripts in markup, try again in 6 seconds
    }
  };

  function showReloadTimeout() {
    setTimeout(checkScripts, 30 * MINUTE); // 30 minutes
  }

  const handleClose = () => {
    showReloadTimeout();
    setShowReloadPopup(false);
  };

  React.useEffect(() => {
    const fetchTranslations = async () => {
      try {
        // fetch content for translations
        const contentList = await GetContent();
        const formattedRes = generateResources(contentList);
        formattedRes.map((r) => i18n.addResourceBundle(r.code, 'default', r.translations));
        // refresh whole app when new content is loaded
        setNonce(generateNonce());
        setResources(contentList);
      } catch (e) {
        console.error(e);
      }
      setResourceReady(true); // ignore loading resource failed, since we have a default local translation anyway;
    };
    void fetchTranslations();

    checkScripts(); // check first time immediately and then set timeout in 30 minutes
  }, []);

  const handleChangeLocaleCode = (code: string): void => {
    CurrentUser.getInstance().setLocale(code);
    setLocaleCode(code);
  };

  // refresh whole app when localeCode changes
  const key = `${nonce}-${localeCode}`;
  // console.log('[ContentProvider]', key);

  const renderAppRefreshCheck = () => {
    return (
      <Modal
        open={showReloadPopup}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{ ...style, width: 400 }}>
          <Typography id="modal-modal-title" variant="h5" component="h5">
            App updated. You should reload page!
          </Typography>
        </Box>
      </Modal>
    );
  };

  return (
    <>
      <Splash name="coriolis" hide={resourceReady} debounce={1000} />
      {resourceReady && (
        <GeneralConfigContext.Provider
          value={{
            localeCode: localeCode,
            setLocaleCode: handleChangeLocaleCode,
            resources: resources,
          }}
        >
          <div key={key}>{children}</div>
        </GeneralConfigContext.Provider>
      )}
      {showReloadPopup && renderAppRefreshCheck()}
    </>
  );
}
